import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  Collapse,
  Button,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { MapProtectedRainforestArea } from "types/map";
import { formatSqFtToKm2 } from "libraries/map";
import MapReferenceCompareTooltip from "./MapReferenceCompareTooltip";

type Props = {
  protectedCorridors: MapProtectedRainforestArea[];
  hiddenCorridorIndices: number[];
  onToggleCorridor: (index: number) => void;
  isMobile: boolean;
};

const MapLegendRainforestPanelCorridors = ({
  protectedCorridors,
  hiddenCorridorIndices,
  onToggleCorridor,
  isMobile,
}: Props) => {
  const [open, setOpen] = useState(!isMobile);

  // Calculate total sqft only for visible corridors
  const totalCorridorSqFt = useMemo(
    () =>
      protectedCorridors.reduce((sum, area, index) => {
        if (hiddenCorridorIndices.includes(index)) return sum;
        return sum + (area.squareFootage || 0);
      }, 0),
    [protectedCorridors, hiddenCorridorIndices]
  );

  if (protectedCorridors.length === 0) return null;

  const corridorColor = protectedCorridors[0].areaFillColor;
  const corridorStroke = protectedCorridors[0].areaStrokeColor;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            width: isMobile ? 16 : 18,
            height: isMobile ? 16 : 18,
            backgroundColor: corridorColor,
            border: `2px solid ${corridorStroke}`,
            borderRadius: 0.5,
            flexShrink: 0,
          }}
        />
        <Typography
          variant={isMobile ? "subtitle2" : "subtitle1"}
          fontWeight="bold"
        >
          Rainforest corridors
        </Typography>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          ml: isMobile ? 3.5 : 4,
          fontSize: isMobile ? "0.7rem" : undefined,
          lineHeight: isMobile ? 1.2 : undefined,
        }}
      >
        Unbroken stretch of intact rainforest
      </Typography>

      {protectedCorridors.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ ml: isMobile ? 3.5 : 4, mt: 0.5 }}
        >
          <SquareFootIcon
            sx={{ fontSize: "0.9rem", color: "text.secondary" }}
          />
          <MapReferenceCompareTooltip squareFootage={totalCorridorSqFt}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
            >
              {formatSqFtToKm2(totalCorridorSqFt)} total
            </Typography>
          </MapReferenceCompareTooltip>
          <Box flexGrow={1} />
          <Tooltip
            placement="top"
            title={open ? "Collapse details" : "Expand details"}
            arrow
          >
            <Button
              size="small"
              color="success"
              onClick={() => setOpen(!open)}
              sx={{ padding: 0.5 }}
              endIcon={
                open ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )
              }
            >
              View
            </Button>
          </Tooltip>
        </Stack>
      )}

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            ml: isMobile ? 3.5 : 4,
            mt: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {protectedCorridors.map((corridor, index) => {
            const isHidden = hiddenCorridorIndices.includes(index);
            return (
              <FormControlLabel
                key={index}
                control={
                  <Switch
                    size="small"
                    checked={!isHidden}
                    color="success"
                    onChange={() => onToggleCorridor(index)}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "text.primary" }}
                    >
                      {corridor.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        ml: 1,
                        textDecoration: isHidden ? "line-through" : "none",
                      }}
                    >
                      {formatSqFtToKm2(corridor.squareFootage || 0)}
                    </Typography>
                  </Box>
                }
                labelPlacement="start"
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  margin: 0,
                  "& .MuiFormControlLabel-label": {
                    width: "100%",
                  },
                }}
              />
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default MapLegendRainforestPanelCorridors;
